import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';

const formatCurrency = (amount) => {
  const val = amount || 0;
  if (val < 0) {
    return `(${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(Math.abs(val))})`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(val);
};

const BalanceSheetSection = ({ 
  title, 
  items, 
  total, 
  totalLabel, 
  headerBgClass, 
  headerTextClass, 
  totalTextClass,
  hoverClass,
  categoryIdMap,
  onDrillDown,
  showAbsolute 
}) => {
  const entries = Object.entries(items || {});
  
  const handleAmountClick = (name, amount) => {
    const categoryId = categoryIdMap && categoryIdMap[name];
    if (categoryId && amount !== 0 && onDrillDown) {
      onDrillDown(categoryId, name, amount);
    }
  };

  const displayAmount = (val) => showAbsolute ? Math.abs(val) : val;

  return (
    <Card className="border-slate-200">
      <CardHeader className={`${headerBgClass} border-b py-3`}>
        <CardTitle className={`font-semibold ${headerTextClass} text-base`}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-3">
        {entries.length === 0 ? (
          <p className="text-slate-500 text-sm py-2 text-center">-</p>
        ) : (
          <Table>
            <TableBody>
              {entries.map(([name, amount]) => {
                const categoryId = categoryIdMap && categoryIdMap[name];
                const isClickable = categoryId && amount !== 0;
                return (
                  <TableRow key={name} className="group">
                    <TableCell className="py-2 text-slate-700">{name}</TableCell>
                    <TableCell className="text-right py-2 tabular-nums">
                      {isClickable ? (
                        <button
                          onClick={() => handleAmountClick(name, amount)}
                          className={`hover:underline cursor-pointer transition-colors ${hoverClass}`}
                        >
                          {formatCurrency(displayAmount(amount))}
                        </button>
                      ) : (
                        formatCurrency(displayAmount(amount))
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <div className="pt-2 border-t flex justify-between text-sm">
          <span className="font-medium text-slate-700">{totalLabel}</span>
          <span className={`font-semibold tabular-nums ${totalTextClass}`}>{formatCurrency(displayAmount(total))}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const BalanceSheet = ({ balanceSheet, onDrillDown }) => {
  if (!balanceSheet) return null;

  const categoryIdMap = balanceSheet.category_id_map || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets Column */}
        <div className="space-y-4">
          <BalanceSheetSection
            title="Current Assets"
            items={balanceSheet.current_assets}
            total={balanceSheet.total_current_assets}
            totalLabel="Total Current Assets"
            headerBgClass="bg-blue-50 border-blue-100"
            headerTextClass="text-blue-900"
            totalTextClass="text-blue-700"
            hoverClass="hover:text-blue-600"
            categoryIdMap={categoryIdMap}
            onDrillDown={onDrillDown}
          />

          <BalanceSheetSection
            title="Fixed Assets"
            items={balanceSheet.fixed_assets}
            total={balanceSheet.total_fixed_assets}
            totalLabel="Total Fixed Assets"
            headerBgClass="bg-indigo-50 border-indigo-100"
            headerTextClass="text-indigo-900"
            totalTextClass="text-indigo-700"
            hoverClass="hover:text-indigo-600"
            categoryIdMap={categoryIdMap}
            onDrillDown={onDrillDown}
          />

          {Object.keys(balanceSheet.other_assets || {}).length > 0 && (
            <BalanceSheetSection
              title="Other Assets"
              items={balanceSheet.other_assets}
              total={balanceSheet.total_other_assets}
              totalLabel="Total Other Assets"
              headerBgClass="bg-cyan-50 border-cyan-100"
              headerTextClass="text-cyan-900"
              totalTextClass="text-cyan-700"
              hoverClass="hover:text-cyan-600"
              categoryIdMap={categoryIdMap}
              onDrillDown={onDrillDown}
            />
          )}
        </div>

        {/* Liabilities Column */}
        <div className="space-y-4">
          <BalanceSheetSection
            title="Current Liabilities"
            items={balanceSheet.current_liabilities}
            total={balanceSheet.total_current_liabilities}
            totalLabel="Total Current Liabilities"
            headerBgClass="bg-amber-50 border-amber-100"
            headerTextClass="text-amber-900"
            totalTextClass="text-amber-700"
            hoverClass="hover:text-amber-600"
            categoryIdMap={categoryIdMap}
            onDrillDown={onDrillDown}
            showAbsolute
          />

          {Object.keys(balanceSheet.long_term_liabilities || {}).length > 0 && (
            <BalanceSheetSection
              title="Long-term Liabilities"
              items={balanceSheet.long_term_liabilities}
              total={balanceSheet.total_long_term_liabilities}
              totalLabel="Total Long-term Liabilities"
              headerBgClass="bg-orange-50 border-orange-100"
              headerTextClass="text-orange-900"
              totalTextClass="text-orange-700"
              hoverClass="hover:text-orange-600"
              categoryIdMap={categoryIdMap}
              onDrillDown={onDrillDown}
              showAbsolute
            />
          )}

          {/* Total Liabilities subtotal */}
          <div className="px-4 py-2 bg-amber-100 rounded flex justify-between text-sm">
            <span className="font-semibold text-amber-900">Total Liabilities</span>
            <span className="font-semibold text-amber-800 tabular-nums">{formatCurrency(Math.abs(balanceSheet.total_liabilities))}</span>
          </div>

          <BalanceSheetSection
            title="Equity"
            items={balanceSheet.equity}
            total={balanceSheet.total_equity}
            totalLabel="Total Equity"
            headerBgClass="bg-purple-50 border-purple-100"
            headerTextClass="text-purple-900"
            totalTextClass="text-purple-700"
            hoverClass="hover:text-purple-600"
            categoryIdMap={categoryIdMap}
            onDrillDown={onDrillDown}
          />
        </div>
      </div>

      {/* Aligned Totals Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-blue-900">TOTAL ASSETS</span>
              <span className="font-semibold text-xl text-blue-700 tabular-nums">{formatCurrency(balanceSheet.total_assets)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="py-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-emerald-900">TOTAL LIABILITIES & EQUITY</span>
              <span className="font-semibold text-xl text-emerald-700 tabular-nums">{formatCurrency(balanceSheet.total_liabilities_and_equity)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { BalanceSheet };
